import './SignupForm.css';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSetRecoilState } from 'recoil';
import { navigationBarState } from '../../../states/NavigationBar';
import { accessTokenState } from '../../../states/Token';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

function SignupForm() {
    const navigate = useNavigate();
    const setNavigationBarState = useSetRecoilState(navigationBarState);
    const setAccessToken = useSetRecoilState(accessTokenState);

    // Form fields
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [nickname, setNickname] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [verificationCode, setVerificationCode] = useState('');

    // Validation states
    const [usernameChecked, setUsernameChecked] = useState(false);
    const [usernameAvailable, setUsernameAvailable] = useState(false);
    const [usernameMessage, setUsernameMessage] = useState('');
    const [phoneAvailable, setPhoneAvailable] = useState(true);
    const [phoneMessage, setPhoneMessage] = useState('');
    const [phoneVerified, setPhoneVerified] = useState(false);
    const [verificationSent, setVerificationSent] = useState(false);
    const [timer, setTimer] = useState(0);
    const [resendTimer, setResendTimer] = useState(0); // 재발송 대기 타이머 (1분)
    const [termsAgreed, setTermsAgreed] = useState(false);

    // Loading states
    const [checkingUsername, setCheckingUsername] = useState(false);
    const [sendingCode, setSendingCode] = useState(false);
    const [verifyingCode, setVerifyingCode] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Refs
    const passwordRef = useRef(null);
    const passwordConfirmRef = useRef(null);
    const nicknameRef = useRef(null);
    const phoneRef = useRef(null);
    const codeRef = useRef(null);

    useEffect(() => {
        // 페이지 진입 시 스크롤 맨 위로 이동
        window.scrollTo(0, 0);

        setNavigationBarState({
            title: '회원가입',
            'left-item': { type: 'back' },
        });
    }, [setNavigationBarState]);

    // Timer for verification code (3분 유효기간)
    useEffect(() => {
        let interval = null;
        if (timer > 0) {
            interval = setInterval(() => {
                setTimer(t => t - 1);
            }, 1000);
        } else if (timer === 0 && verificationSent && !phoneVerified) {
            setVerificationSent(false);
        }
        return () => clearInterval(interval);
    }, [timer, verificationSent, phoneVerified]);

    // Timer for resend cooldown (1분 대기)
    useEffect(() => {
        let interval = null;
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer(t => t - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [resendTimer]);

    // Format timer
    const formatTimer = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Username validation
    const checkUsername = useCallback(async () => {
        if (username.length < 4) {
            setUsernameMessage('아이디는 4자 이상이어야 합니다.');
            setUsernameAvailable(false);
            setUsernameChecked(true);
            return;
        }

        // 형식 검증: 영문 소문자, 숫자, 언더스코어만 허용
        if (!/^[a-z0-9_]+$/.test(username)) {
            setUsernameMessage('영문 소문자, 숫자, 언더스코어(_)만 사용 가능합니다.');
            setUsernameAvailable(false);
            setUsernameChecked(true);
            return;
        }

        setCheckingUsername(true);
        try {
            const response = await axios.get(`/users/check-username/?username=${username}`);
            setUsernameAvailable(response.data.available);
            setUsernameMessage(response.data.message);
            setUsernameChecked(true);
        } catch (error) {
            setUsernameMessage('확인 중 오류가 발생했습니다.');
            setUsernameAvailable(false);
            setUsernameChecked(true);
        } finally {
            setCheckingUsername(false);
        }
    }, [username]);

    // Reset username check when username changes
    useEffect(() => {
        setUsernameChecked(false);
        setUsernameAvailable(false);
        setUsernameMessage('');
    }, [username]);

    // Password validation
    const isPasswordValid = password.length >= 4;
    const doPasswordsMatch = password === passwordConfirm && passwordConfirm.length > 0;

    // Phone number formatting
    const formatPhoneNumber = (value) => {
        const numbers = value.replace(/[^\d]/g, '');
        if (numbers.length <= 3) return numbers;
        if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
        return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
    };

    const handlePhoneChange = (e) => {
        const formatted = formatPhoneNumber(e.target.value);
        setPhoneNumber(formatted);
        setPhoneVerified(false);
        setVerificationSent(false);
        setResendTimer(0);
        setPhoneAvailable(true);
        setPhoneMessage('');
    };

    // Check phone number availability
    const checkPhoneNumber = async (phone) => {
        try {
            const response = await axios.get(`/users/check-phone/?phone_number=${phone}`);
            return response.data.available;
        } catch (error) {
            return true; // 에러 시 일단 진행 허용
        }
    };

    // Send verification code
    const sendVerificationCode = async () => {
        const phone = phoneNumber.replace(/[^\d]/g, '');
        if (phone.length < 10) {
            toast.error('올바른 휴대폰 번호를 입력해주세요.');
            return;
        }

        // 재발송 대기 중인지 확인
        if (resendTimer > 0) {
            toast.error(`${resendTimer}초 후에 다시 시도해주세요.`);
            return;
        }

        setSendingCode(true);
        try {
            // 먼저 휴대폰 번호 중복 체크
            const isAvailable = await checkPhoneNumber(phone);
            if (!isAvailable) {
                setPhoneAvailable(false);
                setPhoneMessage('이미 가입된 휴대폰 번호입니다.');
                setSendingCode(false);
                return;
            }

            const response = await axios.post('/users/send-verification/', {
                phone_number: phone
            });
            if (response.data.success) {
                toast.success('인증번호가 발송되었습니다.');
                setVerificationSent(true);
                setTimer(180); // 3분 유효기간
                setResendTimer(60); // 1분 후 재발송 가능
                setVerificationCode(''); // 이전 인증번호 초기화
                codeRef.current?.focus();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || '인증번호 발송에 실패했습니다.');
        } finally {
            setSendingCode(false);
        }
    };

    // Verify code
    const verifyCode = async () => {
        if (verificationCode.length !== 6) {
            toast.error('6자리 인증번호를 입력해주세요.');
            return;
        }

        const phone = phoneNumber.replace(/[^\d]/g, '');
        setVerifyingCode(true);
        try {
            const response = await axios.post('/users/verify-code/', {
                phone_number: phone,
                verification_code: verificationCode
            });
            if (response.data.success) {
                toast.success('휴대폰 인증이 완료되었습니다.');
                setPhoneVerified(true);
                setTimer(0);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || '인증에 실패했습니다.');
        } finally {
            setVerifyingCode(false);
        }
    };

    // Form validation
    const isFormValid =
        usernameChecked &&
        usernameAvailable &&
        isPasswordValid &&
        doPasswordsMatch &&
        nickname.trim().length > 0 &&
        phoneVerified &&
        termsAgreed;

    // Submit signup
    const handleSubmit = async () => {
        if (!isFormValid) return;

        const phone = phoneNumber.replace(/[^\d]/g, '');
        setSubmitting(true);
        try {
            const response = await axios.post('/users/signup/', {
                username,
                password,
                nickname: nickname.trim(),
                phone_number: phone
            });
            // 회원가입 성공 시 자동 로그인 처리
            if (response.data.access) {
                toast.success(`${nickname.trim()}님 환영합니다!`);
                setAccessToken(response.data);
                // Root로 이동하여 LoggedIn 컴포넌트로 전환
                navigate('/');
            } else {
                toast.error(response.data.message || '회원가입에 실패했습니다.');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || '회원가입에 실패했습니다.');
        } finally {
            setSubmitting(false);
        }
    };

    // Key handlers
    const handleKeyDown = (e, nextRef, action) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (action) {
                action();
            } else if (nextRef?.current) {
                nextRef.current.focus();
            }
        }
    };

    return (
        <div className="signup-page">
            <div className="signup-container">
                <div className="signup-header">
                    <div className="signup-header-description">통번역 자격시험 서비스 이용을 위해<br/>회원정보를 입력해주세요.</div>
                </div>

            <div className="signup-form">
                {/* Account Section */}
                <div className="signup-section">
                    <div className="signup-section-title">계정 정보</div>

                    {/* Username */}
                    <div className="signup-input-group">
                        <label className="signup-input-label">아이디</label>
                        <div className="signup-input-wrapper">
                            <input
                                type="text"
                                className={`signup-input with-button ${usernameChecked ? (usernameAvailable ? 'success' : 'error') : ''}`}
                                placeholder="4자 이상 입력"
                                value={username}
                                onChange={(e) => setUsername(e.target.value.toLowerCase())}
                                onKeyDown={(e) => handleKeyDown(e, null, username.length >= 4 ? checkUsername : null)}
                                maxLength={20}
                            />
                            <button
                                className="signup-input-button"
                                onClick={checkUsername}
                                disabled={username.length < 4 || checkingUsername}
                            >
                                {checkingUsername ? '확인중...' : '중복확인'}
                            </button>
                        </div>
                        {usernameChecked && (
                            usernameAvailable ? (
                                <div className="signup-input-success">{usernameMessage}</div>
                            ) : (
                                <div className="signup-input-error">{usernameMessage}</div>
                            )
                        )}
                        <div className="signup-input-hint">영문 소문자, 숫자, 언더스코어(_)만 사용 가능</div>
                    </div>

                    {/* Password */}
                    <div className="signup-input-group">
                        <label className="signup-input-label">비밀번호</label>
                        <div className="signup-input-wrapper">
                            <input
                                ref={passwordRef}
                                type="password"
                                className={`signup-input ${password.length > 0 ? (isPasswordValid ? 'success' : 'error') : ''}`}
                                placeholder="4자 이상 입력"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                onKeyDown={(e) => handleKeyDown(e, passwordConfirmRef)}
                            />
                            {password.length > 0 && (
                                <span className={`signup-input-icon ${isPasswordValid ? 'success' : 'error'}`}>
                                    {isPasswordValid ? <CheckCircleIcon fontSize="small" /> : <ErrorIcon fontSize="small" />}
                                </span>
                            )}
                        </div>
                        {password.length > 0 && !isPasswordValid && (
                            <div className="signup-input-error">비밀번호는 4자 이상이어야 합니다.</div>
                        )}
                    </div>

                    {/* Password Confirm */}
                    <div className="signup-input-group">
                        <label className="signup-input-label">비밀번호 확인</label>
                        <div className="signup-input-wrapper">
                            <input
                                ref={passwordConfirmRef}
                                type="password"
                                className={`signup-input ${passwordConfirm.length > 0 ? (doPasswordsMatch ? 'success' : 'error') : ''}`}
                                placeholder="비밀번호 재입력"
                                value={passwordConfirm}
                                onChange={(e) => setPasswordConfirm(e.target.value)}
                                onKeyDown={(e) => handleKeyDown(e, nicknameRef)}
                            />
                            {passwordConfirm.length > 0 && (
                                <span className={`signup-input-icon ${doPasswordsMatch ? 'success' : 'error'}`}>
                                    {doPasswordsMatch ? <CheckCircleIcon fontSize="small" /> : <ErrorIcon fontSize="small" />}
                                </span>
                            )}
                        </div>
                        {passwordConfirm.length > 0 && !doPasswordsMatch && (
                            <div className="signup-input-error">비밀번호가 일치하지 않습니다.</div>
                        )}
                    </div>
                </div>

                {/* Personal Info Section */}
                <div className="signup-section">
                    <div className="signup-section-title">개인 정보</div>

                    {/* Name */}
                    <div className="signup-input-group">
                        <label className="signup-input-label">이름</label>
                        <div className="signup-input-wrapper">
                            <input
                                ref={nicknameRef}
                                type="text"
                                className="signup-input"
                                placeholder="실명 입력"
                                value={nickname}
                                onChange={(e) => setNickname(e.target.value)}
                                onKeyDown={(e) => handleKeyDown(e, phoneRef)}
                                maxLength={20}
                            />
                        </div>
                    </div>

                    {/* Phone Number */}
                    <div className="signup-input-group">
                        <label className="signup-input-label">
                            휴대폰 번호
                            {phoneVerified && (
                                <span className="signup-verified-badge">
                                    <CheckCircleIcon style={{ fontSize: 14 }} />
                                    인증완료
                                </span>
                            )}
                        </label>
                        <div className="signup-input-wrapper">
                            <input
                                ref={phoneRef}
                                type="tel"
                                className={`signup-input with-button ${phoneVerified ? 'success' : ''} ${!phoneAvailable ? 'error' : ''}`}
                                placeholder="010-0000-0000"
                                value={phoneNumber}
                                onChange={handlePhoneChange}
                                disabled={phoneVerified}
                                maxLength={13}
                            />
                            {!phoneVerified && (
                                <button
                                    className={`signup-input-button ${verificationSent ? 'outlined' : ''}`}
                                    onClick={sendVerificationCode}
                                    disabled={phoneNumber.replace(/[^\d]/g, '').length < 10 || sendingCode || resendTimer > 0}
                                >
                                    {sendingCode ? '발송중...' :
                                     resendTimer > 0 ? `${resendTimer}초` :
                                     (verificationSent ? '재발송' : '인증요청')}
                                </button>
                            )}
                        </div>
                        {!phoneAvailable && (
                            <div className="signup-phone-error">
                                <span className="signup-phone-error-text">{phoneMessage}</span>
                                <span className="signup-phone-error-link" onClick={() => navigate('/find-account')}>
                                    ID/PW 찾기
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Verification Code */}
                    {verificationSent && !phoneVerified && (
                        <div className="signup-input-group">
                            <label className="signup-input-label">인증번호</label>
                            <div className="signup-verification-row">
                                <div className="signup-input-wrapper signup-verification-input">
                                    <input
                                        ref={codeRef}
                                        type="text"
                                        className="signup-input"
                                        placeholder="6자리 숫자 입력"
                                        value={verificationCode}
                                        onChange={(e) => setVerificationCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                                        onKeyDown={(e) => handleKeyDown(e, null, verificationCode.length === 6 ? verifyCode : null)}
                                        maxLength={6}
                                    />
                                </div>
                                <div className="signup-verification-timer">{formatTimer(timer)}</div>
                                <button
                                    className="signup-verification-button"
                                    onClick={verifyCode}
                                    disabled={verificationCode.length !== 6 || verifyingCode}
                                >
                                    {verifyingCode ? '...' : '확인'}
                                </button>
                            </div>
                            <div className="signup-input-hint">테스트: 123456 입력시 인증 통과</div>
                        </div>
                    )}
                </div>

                {/* Terms */}
                <div className="signup-terms">
                    <label className="signup-terms-checkbox">
                        <input
                            type="checkbox"
                            checked={termsAgreed}
                            onChange={(e) => setTermsAgreed(e.target.checked)}
                        />
                        <span className="signup-terms-text">
                            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">개인정보처리방침</a> 및{' '}
                            <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">서비스 이용약관</a>에 동의합니다.
                        </span>
                    </label>
                </div>

                {/* Submit Button */}
                <button
                    className="signup-submit-button"
                    onClick={handleSubmit}
                    disabled={!isFormValid || submitting}
                >
                    {submitting ? (
                        <>
                            <span className="signup-loading"></span>
                            가입 중...
                        </>
                    ) : '가입하기'}
                </button>

                {/* Login Link */}
                <div className="signup-login-link">
                    이미 계정이 있으신가요? <a onClick={() => navigate('/')}>로그인</a>
                </div>
            </div>
            </div>
        </div>
    );
}

export default SignupForm;
